import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/articles';

const categoryMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryMap[c.slug] = c.name;
});

const coreFaqItems = [
  {
    question: '「羊宫妃那我来草你了」是什么意思？',
    answer: '这是中文二次元社区中一句流行网络用语，结合了对日本声优羊宫妃那（Yomiya Hina）的喜爱和粉丝文化中特有的表达方式。它并非字面攻击性言论，而是一种带有调侃和追捧意味的「整活」行为，体现了粉丝对声优的狂热喜爱。',
  },
  {
    question: '羊宫妃那是谁？',
    answer: '羊宫妃那（Yomiya Hina）是日本新生代声优，出生于2000年3月26日，隶属于青二Production事务所。因在《BanG Dream! It\'s MyGO!!!!!》中为女主角高松灯配音而迅速走红。',
  },
  {
    question: '这句话的来源是什么？',
    answer: '该词条最早出现在中国的弹幕视频网站和社交媒体平台。在MyGO!!!!!动画播出期间，粉丝被羊宫妃那饰演的高松灯角色打动，催生了大量相关迷因。从小众论坛开始传播，经由B站、微博等平台扩散，最终成为二次元圈层标志性用语。',
  },
  {
    question: '为什么「羊宫妃那我来草你了」会流行？',
    answer: '与《BanG Dream! It\'s MyGO!!!!!》动画的巨大成功、羊宫妃那出色的声音演绎、中文二次元社区「扭曲的爱」文化传统，以及社交媒体平台的快速传播效应密切相关。',
  },
  {
    question: '什么是声优偶像化？',
    answer: '声优偶像化是日本动画产业中声优从幕后走到台前的趋势。现代声优不仅需要配音能力，还需具备偶像所需的外形条件、歌唱能力和舞台表现力。羊宫妃那在MyGO!!!!!中的Live演出就是典型案例。',
  },
];

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到"
          description="抱歉，您访问的文章不存在。请返回首页浏览其他精彩内容。"
        />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 3);

  const categoryName = categoryMap[article.category] || article.category;
  const pageUrl = `https://tjcongling.cn/article/${article.slug}`;
  const isCoreArticle = article.slug === 'yangongfei-wo-lai-cao-ni-le';

  const jsonLdItems: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.coverImage,
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      datePublished: article.publishDate,
      publisher: {
        '@type': 'Organization',
        name: '羊宫妃那词条百科',
        url: 'https://tjcongling.cn',
      },
      url: pageUrl,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': pageUrl,
      },
      about: {
        '@type': 'Thing',
        name: article.tags.join(', '),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://tjcongling.cn/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: categoryName,
          item: `https://tjcongling.cn/category/${article.category}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
        },
      ],
    },
  ];

  if (isCoreArticle) {
    jsonLdItems.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: coreFaqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={pageUrl}
        ogType="article"
        ogImage={article.coverImage}
        jsonLd={jsonLdItems}
      />

      <Navbar />

      <main className="pt-16">
        <article>
          <header className="bg-background-100 border-b border-foreground-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </header>

          <section className="bg-background-50 py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 羊宫妃那词条百科`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                <span className="text-sm text-foreground-400">{article.readTime}</span>
                <span className="text-sm text-foreground-400">·</span>
                <span className="text-sm text-foreground-400">{article.author}</span>
                <span className="text-sm text-foreground-400">·</span>
                <span className="text-sm text-foreground-400">{article.publishDate}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>

              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-foreground-100 text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-950
                  prose-ul:text-foreground-700 prose-ol:text-foreground-700
                  prose-li:leading-relaxed prose-li:mb-1.5
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>

              <footer className="mt-12 pt-8 border-t border-foreground-200">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/${article.category}`}
                        className="text-xs px-2.5 py-1 rounded-full bg-foreground-100 text-foreground-600 hover:bg-foreground-200 transition-colors"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                  <div className="flex items-center gap-4">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(pageUrl)}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-500 hover:bg-foreground-200 transition-colors"
                      aria-label="分享到 Twitter"
                    >
                      <i className="ri-twitter-x-line text-sm"></i>
                    </a>
                    <a
                      href={`https://service.weibo.com/share/share.php?url=${encodeURIComponent(pageUrl)}&title=${encodeURIComponent(article.title)}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-500 hover:bg-foreground-200 transition-colors"
                      aria-label="分享到微博"
                    >
                      <i className="ri-weibo-line text-sm"></i>
                    </a>
                  </div>
                </div>
              </footer>
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-20 bg-background-100 border-t border-foreground-100">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-white rounded-lg border border-foreground-100 overflow-hidden transition-all duration-300 hover:border-foreground-200 cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          title={`${ra.title} - 羊宫妃那词条百科`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-foreground-700 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400">{ra.readTime} · {ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}